import { useRef, useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { useNavigate } from "react-router-dom"

import { useQueryContext, useReportPsychologistNameCopy } from "utils/hooks"
import style from "modules/PsyProfile/style.module.scss"
import {
    Competences,
    FAQContainer,
    WhiteBookingBlock,
    Info,
    WhyAlter,
    Qualifications,
    AboutMe,
    Guarantees,
    Booking,
    BookingMobile,
    Breadcrumbs,
} from "modules/PsyProfile/components"
import {
    getDefaultServiceGroup,
    getLastServiceGroup,
    getServiceGroupFromSurvey,
} from "modules/BookingCommon/common/utils"
import { Footer } from "components"
import { useClientContext } from "modules/ClientAuthorization/common/ClientContext"
import { useOnlyFreeSessions } from "pages/FreePsysPage/useOnlyFreeSessions"
import { ServicesGroup } from "utils/helpers/ServicesGroup"
import {
    buildHashIdsQuery,
    findFastBookingWithPsychologist,
    getBookingAvailableWithSlots,
} from "utils/helpers"
import { Analytics, FBProduct } from "utils/analytics"
import { useSearchParams } from "utils/customHooks"
import { Psychologist } from "utils/interfaces"
import { isBrowser } from "utils/infrastructure/ssg-utils"
import { getRawURLFormData } from "utils"
import { AppDispatch } from "utils/store/store"
import { getChatId, getChatList, setChatRole } from "modules/Chat/utils/api"
import { ROLES } from "utils/constants"
import { SurveyResultsStorage } from "modules/NewClientSurvey/common/utils/SurveyResultsStorage"
import { ProfileStoriesNav } from "modules/PsyProfile/components/ProfileStoriesNav"

export function PsyProfile({ psy, showFooter }: { psy: Psychologist; showFooter?: boolean }) {
    const dispatch = useDispatch<AppDispatch>()
    const navigate = useNavigate()
    const searchParams = useSearchParams()
    const [{ userAccount }] = useClientContext()
    const targetRef = useRef<HTMLDivElement>(null)
    const [isVisibleBookingMobile, setIsVisibleBookingMobile] = useState(false)

    const position = searchParams.get("position") ?? "1"
    const whyAlterRef = useRef<HTMLDivElement>(null)
    const bookingQuery = isBrowser() ? (getRawURLFormData(window.location.href) ?? {}) : {}
    const onlyFree = useOnlyFreeSessions()
    const serviceGroups = ServicesGroup.from(psy, onlyFree)
    const groupFromSurvey = getServiceGroupFromSurvey(serviceGroups)
    const bookingAvailable = getBookingAvailableWithSlots(psy)
    const lastFastBookingPsychologist = findFastBookingWithPsychologist(
        userAccount.psychologists,
        psy.hash_id
    )
    const lastServiceGroup = getLastServiceGroup(
        lastFastBookingPsychologist?.last_booking_service_type_id,
        serviceGroups
    )

    const [selectGroup, setSelectGroup] = useState<ServicesGroup | undefined>(
        getDefaultServiceGroup({
            lastUsed: lastServiceGroup,
            fromSurvey: groupFromSurvey,
            defaultGroup: serviceGroups.find((group) =>
                bookingAvailable ? group.availableStatus.ok : group.availableStatusWithCompany
            ),
            bookingAvailable,
        })
    )

    const showPsysStory = searchParams.get("show_psys_story")
    const fromLink = searchParams.get("from_link")
    const hashIdsString = fromLink && new URLSearchParams(fromLink.split("?")[1]).get("hash_ids")
    const hashIds = hashIdsString ? hashIdsString.split(",") : []
    const query = buildHashIdsQuery({
        hashIds: hashIds,
        includeLegalInfo: true,
        resultsKey: undefined,
    })
    const [{ response }] = useQueryContext(query)

    const psyProfiles = hashIds.length ? response?.profiles : SurveyResultsStorage.profiles

    useEffect(() => {
        Analytics.viewprofile({ psy, position })
    }, [psy])

    useReportPsychologistNameCopy(psy.display_name, "profile")

    const handleScroll = () => {
        const target = targetRef.current
        if (target) {
            const rect = target.getBoundingClientRect()
            setIsVisibleBookingMobile(rect.top >= 0 && rect.bottom <= window.innerHeight)
        }
    }

    useEffect(() => {
        window.addEventListener("scroll", handleScroll)
        handleScroll()

        return () => {
            window.removeEventListener("scroll", handleScroll)
        }
    }, [])

    const scrollToElement = () => {
        targetRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })
    }

    const onClickAskPsy = async () => {
        dispatch(setChatRole(ROLES.CLIENT))
        const chats = await dispatch(getChatList(ROLES.CLIENT))

        if (chats) {
            const chat = chats.find((chat) => chat.userId === psy.hash_id)
            if (chat) {
                const chatId = chat.chatId
                navigate(`/chat/${chatId}`)
                return
            }

            const chatId = await dispatch(getChatId(ROLES.CLIENT, psy.hash_id))

            if (chatId) {
                navigate(`/chat/${chatId}`)
                return
            }
        }
        Analytics.email_dialog({
            autoSend: false,
            title: "Задать вопрос психологу",
            psy,
            bookingAvailable,
            position,
        })
    }

    return (
        <>
            {showPsysStory && psyProfiles && psyProfiles.length > 1 && (
                <ProfileStoriesNav
                    psyProfiles={psyProfiles}
                    activePsy={psy}
                    searchParams={searchParams}
                />
            )}
            <div className={style.container}>
                <div className={style.blocks}>
                    <FBProduct psy={psy} />
                    <Info psy={psy} />
                    <AboutMe psy={psy} />
                    <Competences psy={psy} />
                    <Qualifications psy={psy} />
                    <div className={style.mobileBooking}>
                        <Booking
                            ref={targetRef}
                            psy={psy}
                            query={bookingQuery}
                            position={position}
                            serviceGroups={serviceGroups}
                            bookingAvailable={bookingAvailable}
                            selectGroup={selectGroup}
                            dateTestIdPrefix="mobile"
                            onChangeSelectedGroup={setSelectGroup}
                            onClickAskPsy={() => void onClickAskPsy()}
                        />
                    </div>
                    <WhyAlter ref={whyAlterRef} />
                    <Guarantees />
                    <FAQContainer psy={psy} />
                    <WhiteBookingBlock psy={psy} query={bookingQuery} position={position} />
                    <Breadcrumbs psy={psy} />
                </div>
                <div className={style.booking}>
                    <Booking
                        psy={psy}
                        query={bookingQuery}
                        position={position}
                        serviceGroups={serviceGroups}
                        bookingAvailable={bookingAvailable}
                        selectGroup={selectGroup}
                        dateTestIdPrefix="desktop"
                        showStories={!!fromLink && !!psyProfiles}
                        onChangeSelectedGroup={setSelectGroup}
                        onClickAskPsy={() => void onClickAskPsy()}
                    />
                </div>
            </div>
            {showFooter && <Footer />}
            <BookingMobile
                psy={psy}
                selectGroup={selectGroup}
                visibleBooking={isVisibleBookingMobile}
                onClickButton={scrollToElement}
            />
        </>
    )
}
